import type { DebateTopology } from "../../domain/types";

interface Props {
  topology: DebateTopology;
  /** Whether the diagram should render in active accent color. */
  active?: boolean;
  size?: number;
}

const SEATS = 5;
const RADIUS = 22;
const CENTER = 32;

function seatPos(index: number, total: number): { x: number; y: number } {
  const angle = -Math.PI / 2 + (2 * Math.PI * index) / total;
  return { x: CENTER + RADIUS * Math.cos(angle), y: CENTER + RADIUS * Math.sin(angle) };
}

/**
 * Mini SVG glyph for each topology, rendered inside the topology picker cards.
 * - round-robin     → ring of arrows (clockwise rotation)
 * - cross-fire      → two seats facing each other with bidirectional arrows
 * - moderator-led   → star pattern from the central moderator
 * - diverse-poles   → two opposing poles + one mediator orbiting between them
 * - solo-then-debate → row of isolated solo dots above a debate circle
 * - investigation    → ledger grid of cells with one highlighted (the
 *                       "next open cell" the controller just picked)
 */
export function TopologyDiagram({ topology, active = false, size = 64 }: Props) {
  const stroke = active ? "hsl(var(--accent))" : "hsl(var(--fg-muted) / 0.55)";
  const dot = active ? "hsl(var(--accent))" : "hsl(var(--fg-muted) / 0.6)";
  const accent = active ? "hsl(var(--accent-2))" : "hsl(var(--fg-muted) / 0.45)";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-hidden
      className="flex-none"
    >
      <defs>
        <marker
          id={`arrow-${topology}-${active ? "on" : "off"}`}
          viewBox="0 0 6 6"
          refX="5"
          refY="3"
          markerWidth="4"
          markerHeight="4"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={stroke} />
        </marker>
      </defs>

      <circle cx={CENTER} cy={CENTER} r={26} fill="hsl(var(--bg-surface-3) / 0.5)" stroke={stroke} strokeOpacity={0.25} />

      {topology === "round-robin" ? (
        <g>
          {Array.from({ length: SEATS }).map((_, i) => {
            const a = seatPos(i, SEATS);
            const b = seatPos((i + 1) % SEATS, SEATS);
            return (
              <line
                key={i}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={stroke}
                strokeWidth={1.4}
                markerEnd={`url(#arrow-${topology}-${active ? "on" : "off"})`}
                opacity={0.9}
              />
            );
          })}
          {Array.from({ length: SEATS }).map((_, i) => {
            const p = seatPos(i, SEATS);
            return <circle key={`d${i}`} cx={p.x} cy={p.y} r={3} fill={dot} />;
          })}
        </g>
      ) : null}

      {topology === "cross-fire" ? (
        <g>
          {(() => {
            const a = { x: 16, y: 32 };
            const b = { x: 48, y: 32 };
            return (
              <>
                <line
                  x1={a.x + 5}
                  y1={a.y - 4}
                  x2={b.x - 5}
                  y2={b.y - 4}
                  stroke={stroke}
                  strokeWidth={1.6}
                  markerEnd={`url(#arrow-${topology}-${active ? "on" : "off"})`}
                />
                <line
                  x1={b.x - 5}
                  y1={b.y + 4}
                  x2={a.x + 5}
                  y2={a.y + 4}
                  stroke={accent}
                  strokeWidth={1.6}
                  markerEnd={`url(#arrow-${topology}-${active ? "on" : "off"})`}
                />
                <circle cx={a.x} cy={a.y} r={4} fill={dot} />
                <circle cx={b.x} cy={b.y} r={4} fill={dot} />
                <circle cx={CENTER} cy={14} r={2.4} fill={accent} opacity={0.7} />
              </>
            );
          })()}
        </g>
      ) : null}

      {topology === "moderator-led" ? (
        <g>
          {Array.from({ length: SEATS }).map((_, i) => {
            const p = seatPos(i, SEATS);
            return (
              <line
                key={`s${i}`}
                x1={CENTER}
                y1={CENTER}
                x2={p.x}
                y2={p.y}
                stroke={stroke}
                strokeWidth={1.3}
                opacity={0.85}
              />
            );
          })}
          {Array.from({ length: SEATS }).map((_, i) => {
            const p = seatPos(i, SEATS);
            return <circle key={`p${i}`} cx={p.x} cy={p.y} r={3} fill={dot} />;
          })}
          <circle cx={CENTER} cy={CENTER} r={4.2} fill={accent} />
        </g>
      ) : null}

      {topology === "diverse-poles" ? (
        <g>
          {/* Two opposing poles connected by a tense double-line, with two
              satellites near each pole and a single mediator floating
              between them. Visualises forced-pole prompt + later RR. */}
          <line
            x1={14}
            y1={32}
            x2={50}
            y2={32}
            stroke={stroke}
            strokeWidth={1.4}
            opacity={0.7}
            strokeDasharray="3 2"
          />
          <line
            x1={14}
            y1={28}
            x2={50}
            y2={28}
            stroke={accent}
            strokeWidth={1.1}
            opacity={0.5}
            strokeDasharray="2 3"
          />
          <line
            x1={14}
            y1={36}
            x2={50}
            y2={36}
            stroke={accent}
            strokeWidth={1.1}
            opacity={0.5}
            strokeDasharray="2 3"
          />
          <circle cx={14} cy={32} r={4} fill={dot} />
          <circle cx={50} cy={32} r={4} fill={dot} />
          <circle cx={20} cy={20} r={2.4} fill={dot} opacity={0.75} />
          <circle cx={20} cy={44} r={2.4} fill={dot} opacity={0.75} />
          <circle cx={44} cy={20} r={2.4} fill={dot} opacity={0.75} />
          <circle cx={44} cy={44} r={2.4} fill={dot} opacity={0.75} />
          <circle cx={CENTER} cy={CENTER} r={2.6} fill={accent} />
        </g>
      ) : null}

      {topology === "solo-then-debate" ? (
        <g>
          {/* Top row: independent solo dots (round 0).
              Bottom: ring suggesting the optional debate phase. */}
          {[16, 26, 36, 46].map((x, i) => (
            <circle key={`solo${i}`} cx={x} cy={20} r={2.6} fill={dot} opacity={0.85} />
          ))}
          {/* Vertical "fall through to debate" arrows */}
          {[16, 26, 36, 46].map((x, i) => (
            <line
              key={`fall${i}`}
              x1={x}
              y1={24}
              x2={x}
              y2={36}
              stroke={stroke}
              strokeWidth={0.9}
              opacity={0.5}
            />
          ))}
          {/* Debate ring */}
          <circle
            cx={CENTER}
            cy={44}
            r={11}
            fill="none"
            stroke={accent}
            strokeWidth={1.4}
            opacity={0.85}
          />
          {/* Three round-2 speakers on the ring */}
          <circle cx={21} cy={44} r={2.4} fill={accent} />
          <circle cx={CENTER} cy={33} r={2.4} fill={accent} />
          <circle cx={43} cy={44} r={2.4} fill={accent} />
        </g>
      ) : null}

      {topology === "investigation" ? (
        <g>
          {/* 3x3 ledger grid — each cell a tracked entity×aspect; the
              center cell (accent) is the "next open" one the coverage-
              driven controller just selected. */}
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((col) => {
              const x = 20 + col * 12;
              const y = 20 + row * 12;
              const isNext = row === 1 && col === 1;
              return (
                <rect
                  key={`cell-${row}-${col}`}
                  x={x - 4}
                  y={y - 4}
                  width={8}
                  height={8}
                  rx={1.5}
                  fill={isNext ? accent : "none"}
                  stroke={isNext ? accent : stroke}
                  strokeWidth={1.1}
                  opacity={isNext ? 0.95 : 0.55}
                />
              );
            }),
          )}
        </g>
      ) : null}
    </svg>
  );
}
